import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Car, Clock, DollarSign, ShieldCheck, CheckCircle2, ArrowRight, MapPin, FileCheck2, Sunrise, Sunset, HeartHandshake, Loader2 } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import DisclosureBanner from "../components/DisclosureBanner";
import { AVP_SHIFTS, AVP_PAY, AVP_PAY_NOTE, AVP_PAY_POINTS, AVP_PAY_EXAMPLES } from "../lib/avpShifts";

function ApplicantStatus({ token, info, onUpdate }) {
  const [shift, setShift] = useState(info.shift || "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const respond = async (choice) => {
    setBusy(true); setError("");
    try { const { data } = await api.post(`/avp-interest/${token}/respond`, { choice }); onUpdate({ ...info, ...data }); }
    catch (e) { setError(formatApiError(e)); } finally { setBusy(false); }
  };
  const interested = info.response?.startsWith("interested");
  return (
    <div data-testid="avp-applicant-status" className="rounded-2xl border border-amber-500/40 bg-amber-500/5 p-6 sm:p-7">
      <div className="text-xs uppercase tracking-[0.25em] text-amber-400 font-semibold flex items-center gap-2"><ShieldCheck className="w-4 h-4" /> Your AVP application{info.first_name ? ` · ${info.first_name}` : ""}</div>
      {info.response ? (
        <div className="mt-3 flex gap-3">
          {interested ? <CheckCircle2 className="w-6 h-6 text-emerald-400 flex-shrink-0" /> : <HeartHandshake className="w-6 h-6 text-slate-300 flex-shrink-0" />}
          <p data-testid="avp-applicant-ack" className="text-slate-200 text-sm sm:text-base leading-relaxed">
            {interested
              ? <>Thank you for confirming your interest in the AVP program{info.shift_label ? <> (<strong className="text-white">{info.shift_label}</strong>)</> : ""}. Your application is currently <strong className="text-white">awaiting management review</strong>. No additional application is required at this time — feel free to keep reading about the program below.</>
              : "You let us know you're no longer interested. Your record stays on file; if your circumstances change, call 667-434-4392 or reply to our e-mail."}
          </p>
        </div>
      ) : (
        <div className="mt-3">
          <p className="text-slate-200 text-sm sm:text-base">We have your application on file — <strong className="text-white">no need to apply again</strong>. Please let us know whether you're still interested{info.shift_label ? <> in <strong className="text-white">{info.shift_label}</strong></> : ""}.</p>
          {!info.shift && (
            <div className="mt-3 grid sm:grid-cols-2 gap-2">
              {Object.entries(AVP_SHIFTS).map(([k, s]) => <button key={k} type="button" data-testid={`avp-applicant-shift-${k}`} onClick={() => setShift(k)} className={`rounded-xl border p-3 text-sm text-left ${shift === k ? "border-amber-500 bg-amber-500/10 text-white" : "border-slate-700 text-slate-200"}`}><span className="font-bold">{s.time}</span></button>)}
            </div>
          )}
          {error && <p className="mt-2 text-sm text-rose-300">{error}</p>}
          <div className="mt-4 flex flex-col sm:flex-row gap-3">
            <Button data-testid="avp-applicant-yes" disabled={!shift || busy} onClick={() => respond(`interested_${shift}`)} className="bg-amber-500 hover:bg-amber-600 text-slate-950 font-bold uppercase tracking-wider">{busy && <Loader2 className="w-4 h-4 animate-spin mr-2" />} I'm Still Interested</Button>
            <Button data-testid="avp-applicant-no" variant="outline" disabled={busy} onClick={() => window.confirm("Withdraw your AVP application?") && respond("withdraw")} className="border-slate-600 text-slate-200 uppercase tracking-wider">No Longer Interested</Button>
          </div>
        </div>
      )}
    </div>
  );
}

const SHIFT_CARDS = [
  {
    key: "am_9_1", Icon: Sunrise, name: "Morning Priority Shift", time: AVP_SHIFTS.am_9_1.time,
    points: [
      "Report to the FedEx station by 9:00 AM",
      "Target dispatch at approximately 9:30 AM",
      "Priority packages with 10:30 AM and 12:00 PM (noon) delivery commitments",
      "On-time delivery is a critical part of this shift",
    ],
  },
  {
    key: "pm_4_8", Icon: Sunset, name: "Evening Priority Shift", time: AVP_SHIFTS.pm_4_8.time,
    points: [
      "Evening priority and time-definite deliveries",
      "Same $80 guaranteed base + $2 per qualifying stop",
      "Ideal for drivers with daytime commitments",
      "Dispatch details confirmed during onboarding",
    ],
  },
];

const VEHICLE_REQS = [
  "Sedan, SUV, minivan, or pickup truck with a covered, lockable bed",
  "Enclosed and lockable cargo area — packages must be secured out of sight",
  "Current registration and active auto insurance in your name (or approved rental)",
  "Safe, reliable mechanical condition; clean interior with room for packages",
  "Vehicle photos (front, rear, both sides) and VIN photo submitted for review",
];

const DRIVER_REQS = [
  "21 years of age or older with a valid driver's license held 2+ years",
  "Consistent on-time service — an important requirement for remaining eligible for AVP assignments",
  "Pass MVR review, background check, and any required testing",
  "Smartphone for delivery, navigation, and scanning apps",
  "Able to lift up to 50 lbs and enter/exit your vehicle repeatedly",
  "Professional, customer-focused, and committed to on-time service",
];

const STEPS = [
  { t: "Choose your shift", d: "Pick the 9 AM–1 PM morning shift or the 4 PM–8 PM evening shift below." },
  { t: "Complete the AVP application", d: "About 10 minutes: contact, eligibility, availability, and your vehicle details." },
  { t: "Vehicle document review", d: "After our team reviews your application, we send you a private secure upload link for registration, insurance, and vehicle photos." },
  { t: "Onboarding", d: "MVR/background screening, any required testing, and vehicle activation." },
];

export default function AvpLearnMore() {
  const [sp] = useSearchParams();
  const token = sp.get("t");
  const [info, setInfo] = useState(null);
  useEffect(() => { if (token) api.get(`/avp-interest/${token}`).then((r) => setInfo(r.data)).catch(() => setInfo(null)); }, [token]);
  const applicant = !!(token && info);
  const selected = applicant ? info.shift : AVP_SHIFTS[sp.get("shift")] ? sp.get("shift") : null;

  return (
    <div data-testid="avp-learn-more-page">
      <DisclosureBanner />
      <section className="py-14 md:py-20 border-b border-slate-900 gradient-radial grain-overlay">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-12 gap-10 items-start">
          <div className="md:col-span-7">
            <Badge className="bg-amber-500/10 text-amber-400 border border-amber-500/30 uppercase tracking-widest text-[10px] px-3 py-1 rounded-full font-semibold">
              <Car className="w-3 h-3 mr-1.5" /> Alternative Vehicle Program · Baltimore, MD
            </Badge>
            <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-extrabold uppercase tracking-tight leading-[0.95] text-white display">
              Deliver priority packages.<br />In your own vehicle.
            </h1>
            <p className="mt-6 text-slate-300 max-w-xl leading-relaxed">
              The Alternative Vehicle Program (AVP) lets qualified drivers use their own approved personal vehicle to deliver FedEx priority and time-definite packages on a focused four-hour shift. No company truck, no full route — a defined set of priority stops, a guaranteed base, and incentives for on-time work.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              {applicant
                ? <a href="#shifts"><Button data-testid="avp-learn-read-btn" size="lg" variant="outline" className="border-amber-500/50 text-amber-300 hover:bg-amber-500/10 uppercase tracking-wider">Read about the program</Button></a>
                : <a href="#shifts"><Button data-testid="avp-learn-choose-shift-btn" size="lg" className="bg-amber-500 hover:bg-amber-600 text-slate-950 font-bold uppercase tracking-wider px-8">Choose a shift &amp; apply <ArrowRight className="w-4 h-4 ml-2" /></Button></a>}
              {!applicant && <Link to="/careers"><Button size="lg" variant="outline" className="border-slate-600 text-slate-100 hover:bg-slate-900 uppercase tracking-wider">All positions</Button></Link>}
            </div>
            {applicant && <div className="mt-8"><ApplicantStatus token={token} info={info} onUpdate={setInfo} /></div>}
          </div>
          <div className="md:col-span-5 rounded-2xl border border-amber-500/30 bg-amber-500/5 p-7" data-testid="avp-learn-pay-card">
            <div className="text-xs uppercase tracking-[0.25em] text-amber-400 font-semibold flex items-center gap-2"><DollarSign className="w-4 h-4" /> Compensation</div>
            <div className="mt-3 text-2xl font-extrabold text-white display uppercase leading-tight">{AVP_PAY}</div>
            <p className="mt-3 text-sm text-slate-300 leading-relaxed">{AVP_PAY_NOTE}</p>
            <div data-testid="avp-pay-example" className="mt-4 rounded-xl border border-slate-700 bg-slate-950/70 p-4 font-mono text-xs sm:text-sm text-slate-100">
              <div className="grid grid-cols-3 text-[10px] uppercase tracking-widest text-slate-500 mb-2"><span>Qualifying stops</span><span className="text-right">Base + incentives</span><span className="text-right">Total</span></div>
              {AVP_PAY_EXAMPLES.map(([a, b, c], i) => (
                <div key={a} className={`grid grid-cols-3 py-1 ${i === 0 ? "text-amber-300 font-bold" : ""}`}><span>{a}</span><span className="text-right">{b}</span><span className="text-right">{c}</span></div>
              ))}
              <div className="mt-2 pt-2 border-t border-slate-700 text-[11px] text-slate-400 font-sans">A late stop loses only its own $2. The $80 base is never reduced.</div>
            </div>
            <ul className="mt-4 space-y-2 text-sm text-slate-300">
              {AVP_PAY_POINTS.map((p) => <li key={p} className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" /> {p}</li>)}
            </ul>
          </div>
        </div>
      </section>

      <section id="shifts" className="py-14 md:py-20 border-b border-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-xs uppercase tracking-[0.25em] text-amber-400 font-semibold flex items-center gap-2"><Clock className="w-4 h-4" /> Two part-time shifts</div>
          <h2 className="mt-3 text-base md:text-lg text-slate-300 max-w-2xl">{applicant ? "Both shifts share the same compensation structure. Your preferred shift is highlighted — no need to select it again." : "Both shifts are AVP positions with the same compensation structure. Select the one that fits your schedule."}</h2>
          <div className="mt-8 grid md:grid-cols-2 gap-6">
            {SHIFT_CARDS.map((s) => (
              <div key={s.key} data-testid={`avp-learn-shift-${s.key}`} className={`rounded-2xl border p-7 flex flex-col ${selected === s.key ? "border-amber-500 bg-amber-500/5 ring-1 ring-amber-500/40" : "border-slate-800 bg-slate-900/60"}`}>
                <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-amber-400 font-bold"><s.Icon className="w-3.5 h-3.5" /> {s.name}{selected === s.key && <span className="ml-2 rounded-full bg-amber-500 text-slate-950 px-2 py-0.5">Your selection</span>}</div>
                <h3 className="mt-3 text-2xl font-bold uppercase text-white display leading-tight">{AVP_SHIFTS[s.key].label}</h3>
                <div className="mt-3 inline-flex w-fit items-center gap-1 rounded-full border border-slate-700 bg-slate-950 px-2.5 py-1 text-xs text-slate-300"><Clock className="w-3 h-3" /> {s.time}</div>
                <ul className="mt-5 space-y-2">
                  {s.points.map((p) => <li key={p} className="flex gap-2 text-sm text-slate-300"><CheckCircle2 className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" /> {p}</li>)}
                </ul>
                <div className="mt-auto pt-6">
                  {applicant ? <div data-testid={`avp-learn-onfile-${s.key}`} className="text-xs text-slate-500">{selected === s.key ? "This is the shift on your application." : "Prefer this shift instead? Mention it when our team contacts you."}</div> : (
                    <Link to={`/apply/avp?shift=${s.key}`}>
                      <Button data-testid={`avp-learn-apply-${s.key}`} className="w-full bg-amber-500 hover:bg-amber-600 text-slate-950 font-bold uppercase tracking-wider">Apply · {AVP_SHIFTS[s.key].short} <ArrowRight className="w-4 h-4 ml-2" /></Button>
                    </Link>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-14 md:py-20 border-b border-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-2 gap-10">
          <div data-testid="avp-learn-vehicle-reqs">
            <div className="text-xs uppercase tracking-[0.25em] text-amber-400 font-semibold flex items-center gap-2"><Car className="w-4 h-4" /> Qualifying personal vehicle</div>
            <ul className="mt-5 space-y-2.5">{VEHICLE_REQS.map((r) => <li key={r} className="flex gap-2 text-sm text-slate-300"><CheckCircle2 className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" /> {r}</li>)}</ul>
            <p className="mt-4 text-xs text-slate-500">Vehicle approval is determined after document and photo review. Submitting an application does not guarantee vehicle approval.</p>
          </div>
          <div data-testid="avp-learn-driver-reqs">
            <div className="text-xs uppercase tracking-[0.25em] text-amber-400 font-semibold flex items-center gap-2"><ShieldCheck className="w-4 h-4" /> Driver qualifications &amp; expectations</div>
            <ul className="mt-5 space-y-2.5">{DRIVER_REQS.map((r) => <li key={r} className="flex gap-2 text-sm text-slate-300"><ShieldCheck className="w-4 h-4 text-slate-500 flex-shrink-0 mt-0.5" /> {r}</li>)}</ul>
            <p className="mt-4 text-xs text-slate-500">Screenings are initiated only after a conditional offer. Integrity Delivered, Inc. is an Equal Opportunity Employer.</p>
          </div>
        </div>
      </section>

      <section className="py-14 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-xs uppercase tracking-[0.25em] text-amber-400 font-semibold flex items-center gap-2"><FileCheck2 className="w-4 h-4" /> {applicant ? "Where you are in the process" : "How to apply"}</div>
          <ol className="mt-6 grid md:grid-cols-4 gap-5">
            {STEPS.map((s, i) => (
              <li key={s.t} className={`rounded-2xl border p-6 ${applicant && i === 0 ? "border-emerald-500/40 bg-emerald-500/5" : "border-slate-800 bg-slate-900/60"}`}>
                <div className="text-3xl font-extrabold text-amber-400 display">{i + 1}</div>
                <div className="mt-2 font-bold text-white display uppercase tracking-tight">{applicant && i === 0 ? "Application received" : s.t}</div>
                <p className="mt-2 text-sm text-slate-400 leading-relaxed">{applicant && i === 0 ? "You've already completed this step — no need to apply again." : s.d}</p>
              </li>
            ))}
          </ol>
          {!applicant && (
          <div className="mt-10 flex flex-col sm:flex-row gap-3">
            <Link to="/apply/avp?shift=am_9_1"><Button data-testid="avp-learn-bottom-apply-am" size="lg" className="bg-amber-500 hover:bg-amber-600 text-slate-950 font-bold uppercase tracking-wider">Apply · 9 AM – 1 PM</Button></Link>
            <Link to="/apply/avp?shift=pm_4_8"><Button data-testid="avp-learn-bottom-apply-pm" size="lg" variant="outline" className="border-amber-500/50 text-amber-300 hover:bg-amber-500/10 uppercase tracking-wider">Apply · 4 PM – 8 PM</Button></Link>
          </div>
          )}
          <p className="mt-6 text-sm text-slate-400 flex items-center gap-2"><MapPin className="w-4 h-4 text-amber-400" /> Questions? Call or text <a href="tel:+16674344392" className="text-amber-400">667-434-4392</a> (daily 8 AM – 8 PM).</p>
        </div>
      </section>
    </div>
  );
}
